import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#clearUpsellLoading);
    document.addEventListener(ThemeEvents.cartError, this.#clearUpsellLoading);
    this.addEventListener('submit', this.#handleSubmit);
    window.addEventListener('pageshow', this.#clearCheckoutLoading);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#clearUpsellLoading);
    document.removeEventListener(ThemeEvents.cartError, this.#clearUpsellLoading);
    this.removeEventListener('submit', this.#handleSubmit);
    window.removeEventListener('pageshow', this.#clearCheckoutLoading);
  }

  /**
   * Loading states for the drawer's two submit buttons. Both only add a class or
   * attribute — never `disabled` — so the submission itself is left alone.
   * - Checkout: shows a spinner while the browser navigates to checkout.
   * - An upsell "Add" pill: shows a spinner until the cart update re-renders the drawer.
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    const button = event.submitter;
    if (!(button instanceof HTMLButtonElement)) return;

    if (button.classList.contains('cart-checkout')) {
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
    } else if (button.classList.contains('cart-upsell__add-btn')) {
      button.setAttribute('aria-busy', 'true');
    }
  };

  #clearUpsellLoading = () => {
    for (const button of this.querySelectorAll('.cart-upsell__add-btn[aria-busy]')) {
      button.removeAttribute('aria-busy');
    }
  };

  /** Coming back from checkout restores the page from the back/forward cache, spinner and all. */
  #clearCheckoutLoading = () => {
    const button = this.querySelector('.cart-checkout.is-loading');
    button?.classList.remove('is-loading');
    button?.removeAttribute('aria-busy');
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
